/**
 * What a WebXR compositor shows during an `immersive-ar` session: the XR
 * layer and the DOM overlay, nothing else. Native draws the camera behind
 * the web view, so the rest of the page is hidden and its backgrounds made
 * transparent for the session.
 */

const STYLE_ID = 'native-xr-presentation';
const OVERLAY_ATTR = 'data-native-xr-overlay';
const CSS = `
html[data-native-xr], html[data-native-xr] body { background: transparent !important; touch-action: none; overscroll-behavior: none; }
html[data-native-xr] body * { visibility: hidden; }
html[data-native-xr] [${OVERLAY_ATTR}] * { visibility: inherit; }
`;

function setStyles(el: HTMLElement, styles: Record<string, string>): () => void {
    const saved = Object.keys(styles).map(name => [name, el.style.getPropertyValue(name)] as const);
    for (const [name, value] of Object.entries(styles)) el.style.setProperty(name, value);
    return () => { for (const [name, value] of saved) el.style.setProperty(name, value); };
}

/**
 * Show only `canvas` and `overlayRoot` over the camera. The canvas takes no
 * pointer events, as under a real compositor: touches become XR input.
 * Returns the function that restores the page.
 */
export function presentOverCamera(canvas: HTMLCanvasElement, overlayRoot: HTMLElement | null): () => void {
    if (!document.getElementById(STYLE_ID)) {
        const style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = CSS;
        document.head.append(style);
    }
    const html = document.documentElement;
    html.setAttribute('data-native-xr', '');
    const restores = [setStyles(canvas, { visibility: 'visible', 'pointer-events': 'none' })];
    if (overlayRoot) {
        overlayRoot.setAttribute(OVERLAY_ATTR, '');
        restores.push(setStyles(overlayRoot, { visibility: 'visible' }), () => overlayRoot.removeAttribute(OVERLAY_ATTR));
    }
    return () => {
        html.removeAttribute('data-native-xr');
        for (const restore of restores) restore();
    };
}
